<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="executive-offer-bench"
      class="mb-2"
    />
    <ChartContent
      height="131px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBenchmarkChart"
      class="px-0 pb-0"
      :link="{
        name: 'Hiring Report',
        to: 'hiring-report',
      }"
    >
      <BarChart :id="`${widgetId}-chart`" :data="chartData" :options="chartOptions" :plugins="plugins" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CanvasDrawBenchmarkPlugin } from '@/api/helpers/insights/chartPlugins';
import { formatTooltipValue } from '@/helpers/formatters';
import { basicChartTooltipPluginConfig, benchmarkPluginPercentOffset, hiddenYScaleConfig } from '@/api/helpers/insights/commonChartConfigs';

const OFFER_ACCPT_RATE_MAX = 85;
const OFFER_ACCPT_RATE_MIN = 75;
const CHART_MAX = 100;

const props = defineProps({
  offerAcceptanceRate: {
    type: Number,
    required: true,
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'Offer Acceptance Rate vs Benchmark';

const widgetId = 'offer-acceptance-rate-benchmark';

const {
  brandPrimary,
  yellow500,
  grey400,
  textDarkest,
  white,
} = useColors();

const noWidgetData = computed(() => !props.offerAcceptanceRate);

const chartData = computed(() => {
  const { offerAcceptanceRate } = props || {};

  return {
    labels: ['Acceptance Rate'],
    datasets: [
      {
        label: '',
        backgroundColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        borderColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: [
          offerAcceptanceRate,
        ],
      },
    ],
  };
});

const plugins = computed(() => [
  ChartDataLabels,
  CanvasDrawBenchmarkPlugin({
    benchmarkOptions: {
      min: OFFER_ACCPT_RATE_MIN,
      max: OFFER_ACCPT_RATE_MAX,
      ...benchmarkPluginPercentOffset,
    },
    scaleMax: CHART_MAX,
    chartId: `${widgetId}-chart`,
  }),
]);

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: basicChartTooltipPluginConfig(chartData, widgetId),
    datalabels: {
      color: function colorCallback(context) {
        const max = CHART_MAX;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        return percentOfSpace < 20 ? textDarkest.value : white.value;
      },
      anchor: 'end',
      align: function alignCallback(context) {
        const max = CHART_MAX;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        if (Number.isNaN(percentOfSpace)) return 'end';

        return percentOfSpace < 20 ? 'end' : 'start';
      },
      formatter: function formatterCallback(value) {
        if (Number.isNaN(value)) return '';
        return formatTooltipValue(value, 'percent');
      },
    },
  },
  scales: {
    x: {
      max: CHART_MAX,
      title: {
        display: false,
      },
      beginAtZero: true,
      border: {
        display: false,
        dash: [2, 3],
      },
      grid: {
        display: true,
        color: grey400.value,
        tickLength: 0,
      },
      ticks: {
        padding: 24,
        maxRotation: 0,
        stepSize: 5,
        callback: function xTickCallback(value) {
          return `${value}%`;
        },
      },
    },
    y: hiddenYScaleConfig,
  },
}));
</script>

<style lang="less" scoped>
:deep(.chart-error-image) {
  height: 131px !important;
  max-height: 131px !important;
  padding: 16px 0px !important;
  margin: 0 !important;
}
</style>
